import React, { useState, useEffect, useMemo } from 'react';
import { useIonViewWillEnter, IonContent, IonSlides, IonSlide, IonIcon, IonButton } from '@ionic/react';
import {
  medkit,
  medkitOutline as rentalIcon,
  eyedropOutline as vaccineIcon,
  list,
  calendarClear,
  calendarNumber,
  cartOutline,
  navigateOutline,
  personOutline,
  arrowForwardOutline,
  locationOutline,
  callOutline,
  logoWhatsapp,
  timeOutline,
  documentTextOutline,
  flaskOutline,
  logoFacebook,
  logoInstagram,
  globeOutline,
} from 'ionicons/icons';

import HomeHeader from '../components/Home/HomeHeader';
import FadeSlider from '../components/Home/FadeSlider';
import HomeIntro from '../components/Home/HomeIntro';
import useOptions from '../hooks/Shared/useOptions';
import useShops from '../hooks/Home/useShops';

import SystemResource from '../resources/System';
import ProfileService from '../services/profile';
import ReservationsService from '../services/reservations';
import OrderService from '../services/order';
import ShopsService from '../services/shops';
import EventService from '../services/shopevent';
import FidelityCardService from '../services/fidelityCard';
import StaffService from '../services/staff';

const DesktopHomePage = (props) => {
  const { loader, history, slot, fpsConfigs } = props;

  const [reservations, setReservations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [events, setEvents] = useState([]);

  const [shops, selected, loadShops] = useShops();
  const [options, slides, loadOptions] = useOptions();

  // Le ricette inviate dall'app sono attive per default: solo il valore '0' le disabilita
  const prescriptionsEnabled = options?.prescriptions !== '0';

  const [staff, setStaff] = useState([]);

  const select_service_string = options?.select_service;
  const [hydratedServices, setHydratedServices] = useState([]);

  // Parse select_service once to avoid recreating array on every render
  const select_service = useMemo(() => {
    try {
      return select_service_string ? JSON.parse(select_service_string) : [];
    } catch (error) {
      console.error('Failed to parse select_service:', error);
      return [];
    }
  }, [select_service_string]);

  useEffect(() => {
    // select_service è memoizzato con useMemo: riferimento stabile, sicuro come dipendenza
    const hydrateServices = async () => {
      if (select_service.length > 0) {
        try {
          const promises = select_service.map(async (service) => {
            try {
              const response = await ReservationsService.findOne({ id: service.id });
              return { ...service, ...response.data.service };
            } catch (error) {
              console.error(`Failed to hydrate service ${service.id}:`, error);
              // Return service without hydration if API fails
              return service;
            }
          });
          const services = await Promise.all(promises);
          setHydratedServices(services);
        } catch (error) {
          console.error('Failed to hydrate services:', error);
          setHydratedServices(select_service);
        }
      } else {
        setHydratedServices([]);
      }
    };

    hydrateServices();
    // select_service è stabile (useMemo su select_service_string): nessun loop infinito
  }, [select_service]);

  useIonViewWillEnter(() => {
    loader.current.show();
    loadOptions();
    loadShops();
    getReservation();
    getOrders();
    checkLoginStatus();
    fidelity();
    loadEvents();
    loadStaff();
    loader.current.dismiss();
  }, []);

  const loadStaff = async () => {
    try {
      const response = await StaffService.get();
      if (response.data && Array.isArray(response.data)) {
        setStaff(response.data);
      } else {
        setStaff([]);
      }
    } catch (error) {
      console.error(error);
      setStaff([]);
    }
  };

  const loadEvents = async () => {
    try {
      const {
        data: { data: eventsData },
      } = await EventService.get();
      setEvents(eventsData || []);
    } catch (error) {
      console.error(error);
      setEvents([]);
    }
  };

  const openCall = () => {
    if (options?.call_number) {
      window.open(`tel:${options.call_number}`, '_system');
    }
  };

  const openChat = () => {
    if (options?.whatsapp_number) {
      const whatsappText = options?.whatsapp_text ?? '';
      window.open(`https://wa.me/${options.whatsapp_number}?text=${encodeURI(whatsappText)}`, '_system');
    }
  };

  const openFacebook = () => {
    if (options?.facebook) {
      window.open(options.facebook, '_system');
    }
  };

  const openInstagram = () => {
    if (options?.instagram) {
      window.open(options.instagram, '_system');
    }
  };

  const openWebsite = () => {
    if (options?.site_url) {
      window.open(options.site_url, '_system');
    }
  };

  const openNavigation = () => {
    if (options?.shop_full_address) {
      // Encode the address for Google Maps
      const encodedAddress = encodeURIComponent(options.shop_full_address);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_system');
    }
  };

  const fidelity = async () => {
    try {
      SystemResource.checkVersion();
      await FidelityCardService.getMain();
    } catch (error) {
      if (error.response?.status !== 401) console.error(error);
    }
  };

  const saveShop = (val) => {
    const shop_id = { shop_id: val };
    const loadData = async () => {
      try {
        await ShopsService.update(shop_id);
      } catch (error) {
        console.error(error);
      }
    };

    if (selected !== undefined && val !== selected) {
      loadData();
    }
  };

  const getReservation = async () => {
    try {
      const response = await ReservationsService.all();
      setReservations(response.data);
    } catch (error) {
      if (error.response?.status !== 401) console.error(error);
    }
  };

  const getOrders = async () => {
    try {
      const response = await OrderService.get();
      setOrders(response.data.data);
    } catch (error) {
      if (error.response?.status !== 401) console.error(error);
    }
  };

  const checkLoginStatus = async () => {
    try {
      const response = await ProfileService.me();
      setIsLoggedIn(!!response);
    } catch (error) {
      setIsLoggedIn(false);
    }
  };

  const truncateText = (text, wordLimit = 15) => {
    if (!text) return '';
    const words = text.replace(/<[^>]+>/g, '').split(' ');
    return words.length > wordLimit ? words.slice(0, wordLimit).join(' ') + '...' : words.join(' ');
  };

  const serviceSlideOpts = {
    slidesPerView: 3,
    spaceBetween: 24,
    pagination: { clickable: true },
    breakpoints: {
      1024: { slidesPerView: 3 },
      1280: { slidesPerView: 4 },
    },
  };

  const staffSlideOpts = {
    slidesPerView: 4,
    spaceBetween: 24,
    pagination: { clickable: true },
    breakpoints: {
      1024: { slidesPerView: 3 },
      1280: { slidesPerView: 4 },
      1600: { slidesPerView: 5 },
    },
  };

  const eventSlideOpts = {
    slidesPerView: 3,
    spaceBetween: 24,
    pagination: { clickable: true },
    breakpoints: {
      1024: { slidesPerView: 2 },
      1280: { slidesPerView: 3 },
    },
  };

  // Dal Task 17 l'annullamento è logico e `find` restituisce anche le prenotazioni
  // annullate: senza filtro il contatore le conterebbe e il cliente che ha appena
  // annullato leggerebbe ancora "1 Prenotazioni".
  const activeReservations = reservations.filter((r) => r.status !== 'cancelled');

  // Build events with unique dates for display
  const eventsWithDates = events.reduce((acc, event) => {
    const uniqueDates = Array.from(new Set(event.availabilities ? event.availabilities.map((a) => a.start_at) : []));
    uniqueDates.forEach((start_at) => {
      const date = new Date(start_at);
      acc.push({
        ...event,
        displayDate: date.toLocaleDateString('it-IT'),
        rawDate: date,
        formattedDate: `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}`,
      });
    });
    return acc;
  }, []);

  return (
    <>
      <HomeHeader
        options={options}
        selected={selected}
        saveShop={saveShop}
        shops={shops}
        history={history}
        slot={slot}
        fpsConfigs={fpsConfigs}
      />

      <IonContent id="homepage" className="ion-no-padding desktop-homepage">
        <HomeIntro title={options?.home_title} text={options?.home_text} color={options?.home_text_color} />

        {/* ===== HERO SLIDER ===== */}
        {slides && slides.length > 0 && (
          <section className="dhp-hero">
            <FadeSlider delay={8000} speed={1000} className="dhp-hero-slider">
              {slides.map((slide, index) => {
                // Preferisci immagine desktop se disponibile, altrimenti usa mobile
                const imageUrl = slide.image_desktop?.length > 0 ? slide.image_desktop : slide.image;
                // Cliccabile solo con un link valido
                const hasLink = slide.url?.trim() && slide.url.trim() !== '#';
                return (
                  imageUrl?.length > 0 &&
                  (hasLink ? (
                    <a key={index} href={slide.url} className="dhp-hero-link">
                      <img alt={slide.alt ?? ''} src={imageUrl} className="dhp-hero-img" />
                    </a>
                  ) : (
                    <div key={index} className="dhp-hero-link">
                      <img alt={slide.alt ?? ''} src={imageUrl} className="dhp-hero-img" />
                    </div>
                  ))
                );
              })}
            </FadeSlider>
          </section>
        )}

        {/* ===== QUICK ACTIONS ===== */}
        <section className="dhp-section dhp-quick-actions">
          <div className="dhp-container">
            <div className="dhp-actions-row">
              {fpsConfigs?.products > 0 && options?.buy_products_in_app === '1' && (
                <>
                  {prescriptionsEnabled && (
                    <div className="dhp-action-item" onClick={() => history.push('/app/prescription')}>
                      <div className="dhp-action-icon purple">
                        <IonIcon icon={medkit} />
                      </div>

                      <span>Ricette</span>
                    </div>
                  )}

                  <div className="dhp-action-item" onClick={() => history.push('/app/category')}>
                    <div className="dhp-action-icon yellow">
                      <IonIcon icon={list} />
                    </div>

                    <span>Prodotti</span>
                  </div>
                </>
              )}

              {fpsConfigs?.prescription_request > 0 && prescriptionsEnabled && (
                <div className="dhp-action-item" onClick={() => history.push('/app/prescription-request')}>
                  <div className="dhp-action-icon purple">
                    <IonIcon icon={documentTextOutline} />
                  </div>

                  <span>Invia Ricetta</span>
                </div>
              )}

              {fpsConfigs?.services > 0 && (
                <>
                  <div className="dhp-action-item" onClick={() => history.push('/app/services')}>
                    <div className="dhp-action-icon blue">
                      <IonIcon icon={calendarClear} />
                    </div>

                    <span>Servizi</span>
                  </div>

                  <div className="dhp-action-item" onClick={() => history.push('/app/rentals')}>
                    <div className="dhp-action-icon green">
                      <IonIcon icon={rentalIcon} />
                    </div>

                    <span>Noleggio</span>
                  </div>

                  {fpsConfigs?.vaccines > 0 && (
                    <div className="dhp-action-item" onClick={() => history.push('/app/vaccini')}>
                      <div className="dhp-action-icon teal">
                        <IonIcon icon={vaccineIcon} />
                      </div>

                      <span>Vaccini</span>
                    </div>
                  )}

                  <div className="dhp-action-item" onClick={() => history.push('/app/events')}>
                    <div className="dhp-action-icon coral">
                      <IonIcon icon={calendarNumber} />
                    </div>

                    <span>Eventi</span>
                  </div>
                </>
              )}

              {fpsConfigs?.services > 0 && (
                <div
                  className="dhp-action-item"
                  onClick={() => history.push(isLoggedIn ? '/app/reservations' : '/auth/login')}
                >
                  <div className="dhp-action-icon green">
                    <IonIcon icon={personOutline} />
                  </div>

                  <span>Prenotazioni</span>
                </div>
              )}

              {options?.turni && (
                <div className="dhp-action-item" onClick={() => history.push('/app/shift')}>
                  <div className="dhp-action-icon pink">
                    <IonIcon icon={documentTextOutline} />
                  </div>

                  <span>Turni</span>
                </div>
              )}

              {fpsConfigs?.products > 0 && options?.buy_products_in_app === '1' && options?.galenica === '1' && (
                <div className="dhp-action-item" onClick={() => history.push('/app/galenica')}>
                  <div className="dhp-action-icon light-yellow">
                    <IonIcon icon={flaskOutline} />
                  </div>

                  <span>Preparazione galenica</span>
                </div>
              )}

              {fpsConfigs?.products > 0 && options?.buy_products_in_app === '1' && (
                <div className="dhp-action-item" onClick={() => history.push('/app/cart')}>
                  <div className="dhp-action-icon light-blue">
                    <IonIcon icon={cartOutline} />
                  </div>

                  <span>Carrello</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ===== SERVIZI ===== */}
        {fpsConfigs?.services > 0 && select_service.length > 0 && (
          <section className="dhp-section dhp-services-section">
            <div className="dhp-container">
              <div className="dhp-section-header">
                <h2>I Nostri Servizi</h2>
                <IonButton fill="clear" color="primary" onClick={() => history.push('/app/services')}>
                  Tutti i servizi <IonIcon icon={arrowForwardOutline} />
                </IonButton>
              </div>

              <IonSlides pager={true} options={serviceSlideOpts} className="dhp-services-slider">
                {hydratedServices.map((service) => (
                  <IonSlide key={service.id}>
                    <div className="dhp-service-card" onClick={() => history.push(`/app/reservations/${service.id}`)}>
                      {service.image_url ? (
                        <div className="dhp-service-card-img">
                          <img
                            src={service.image_url}
                            alt={service.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="dhp-service-card-img dhp-service-card-img-placeholder">
                          <IonIcon icon={calendarClear} />
                        </div>
                      )}

                      <div className="dhp-service-card-body">
                        <h3>{service.name}</h3>
                        <p className="dhp-service-desc">{truncateText(service.description)}</p>
                        <div className="dhp-service-footer">
                          <span className="dhp-service-price">{service.price} €</span>
                          <IonButton size="small" fill="solid" color="primary">
                            Prenota
                          </IonButton>
                        </div>
                      </div>
                    </div>
                  </IonSlide>
                ))}
              </IonSlides>
            </div>
          </section>
        )}

        {/* ===== STAFF ===== */}
        {staff.length > 0 && (
          <section className="dhp-section dhp-staff-section">
            <div className="dhp-container">
              <div className="dhp-section-header">
                <h2>Il Nostro Staff</h2>
              </div>

              <IonSlides pager={true} options={staffSlideOpts} className="dhp-staff-slider">
                {staff.map((member, index) => (
                  <IonSlide key={index}>
                    <div className="dhp-staff-card">
                      <div className="dhp-staff-photo">
                        {member.image ? (
                          <img
                            src={member.image}
                            alt={member.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              // Show placeholder sibling
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}

                        <div className="dhp-staff-photo-placeholder" style={member.image ? { display: 'none' } : {}}>
                          <IonIcon icon={personOutline} />
                        </div>
                      </div>

                      <h3>{member.name}</h3>
                      <p className="dhp-staff-title">{member.role || member.title}</p>
                    </div>
                  </IonSlide>
                ))}
              </IonSlides>
            </div>
          </section>
        )}

        {/* ===== ORDINI & PRENOTAZIONI ===== */}
        <section className="dhp-section dhp-bookings-section">
          <div className="dhp-container">
            <div className="dhp-section-header">
              <h2>
                {fpsConfigs?.products > 0 && options?.buy_products_in_app === '1' && fpsConfigs?.services > 0
                  ? 'Ordini & Prenotazioni'
                  : fpsConfigs?.products > 0 && options?.buy_products_in_app === '1'
                    ? 'Ordini'
                    : fpsConfigs?.services > 0
                      ? 'Prenotazioni'
                      : ''}
              </h2>
            </div>

            <div className="dhp-bookings-grid">
              {fpsConfigs?.services > 0 && (
                <div
                  className="dhp-booking-card dhp-booking-reservations"
                  onClick={() => history.push(isLoggedIn ? '/app/reservations' : '/auth/login')}
                >
                  <div className="dhp-booking-card-inner">
                    <h3>Prenotazioni</h3>
                    <span className="dhp-booking-badge">
                      {isLoggedIn ? `${activeReservations.length} Prenotazioni` : 'Accedi'}
                    </span>
                  </div>
                </div>
              )}

              {fpsConfigs?.products > 0 && options?.buy_products_in_app === '1' && (
                <div
                  className="dhp-booking-card dhp-booking-orders"
                  onClick={() => history.push(isLoggedIn ? '/app/orders' : '/auth/login')}
                >
                  <div className="dhp-booking-card-inner">
                    <h3>{isLoggedIn ? 'Ordine in corso' : 'Ordini'}</h3>
                    <span className="dhp-booking-badge">{isLoggedIn ? `${orders.length} Ordini` : 'Accedi'}</span>
                  </div>
                </div>
              )}

              {fpsConfigs?.prescription_request > 0 && prescriptionsEnabled && (
                <div
                  className="dhp-booking-card dhp-booking-orders"
                  onClick={() => history.push(isLoggedIn ? '/app/prescription-request' : '/auth/login')}
                >
                  <div className="dhp-booking-card-inner">
                    <h3>Ricette</h3>
                    <span className="dhp-booking-badge">Invia ricetta</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ===== EVENTI ===== */}
        {fpsConfigs?.services > 0 && eventsWithDates.length > 0 && (
          <section className="dhp-section dhp-events-section">
            <div className="dhp-container">
              <div className="dhp-section-header">
                <h2>Eventi</h2>
                <IonButton fill="clear" color="primary" onClick={() => history.push('/app/events')}>
                  Tutti gli eventi <IonIcon icon={arrowForwardOutline} />
                </IonButton>
              </div>

              <IonSlides pager={true} options={eventSlideOpts} className="dhp-events-slider">
                {eventsWithDates.slice(0, 9).map((event, index) => (
                  <IonSlide key={`${event.id}-${index}`}>
                    <div
                      className="dhp-event-card"
                      onClick={() => history.push(`/app/events/${event.id}/${event.formattedDate}`)}
                    >
                      <div className="dhp-event-date-badge">
                        <span className="dhp-event-day">{event.rawDate.getDate()}</span>
                        <span className="dhp-event-month">
                          {event.rawDate.toLocaleDateString('it-IT', { month: 'short' }).toUpperCase()}
                        </span>
                      </div>

                      <div className="dhp-event-info">
                        <h3>{event.name}</h3>
                        <p>{event.displayDate}</p>
                      </div>

                      <IonButton size="small" fill="outline" color="primary">
                        Scopri
                      </IonButton>
                    </div>
                  </IonSlide>
                ))}
              </IonSlides>
            </div>
          </section>
        )}

        {/* ===== DOVE SIAMO & CONTATTI ===== */}
        <section className="dhp-section dhp-location-section">
          <div className="dhp-container">
            <div className="dhp-section-header">
              <h2>Dove Siamo</h2>
            </div>

            <div className="dhp-location-grid">
              <div className="dhp-location-contacts">
                <h3>{options?.shop_name}</h3>
                {options?.shop_full_address && (
                  <div className="dhp-contact-row dhp-contact-clickable" onClick={openNavigation}>
                    <IonIcon icon={locationOutline} />
                    <span>Indirizzo: {options.shop_full_address}</span>
                    <IonIcon icon={navigateOutline} className="dhp-navigation-icon" />
                  </div>
                )}

                {options?.call_number && (
                  <div className="dhp-contact-row dhp-contact-clickable" onClick={openCall}>
                    <IonIcon icon={callOutline} />
                    <span>Telefono: {options.call_number}</span>
                  </div>
                )}

                {options?.whatsapp_number && (
                  <div className="dhp-contact-row dhp-contact-clickable" onClick={openChat}>
                    <IonIcon icon={logoWhatsapp} />
                    <span>Whatsapp: {options.whatsapp_number}</span>
                  </div>
                )}

                {options?.open_on && (
                  <div className="dhp-contact-row">
                    <IonIcon icon={timeOutline} />
                    <span>Orari: {options.open_on}</span>
                  </div>
                )}

                {options?.site_url && (
                  <div className="dhp-contact-row" onClick={openWebsite}>
                    <IonIcon icon={globeOutline} />
                    <span>Sito internet: {options.site_url}</span>
                  </div>
                )}

                {(options?.facebook || options?.instagram || options?.site_url) && (
                  <div className="dhp-contact-row dhp-social-links">
                    <span className="dhp-social-label">Seguici su:</span>
                    <div className="dhp-social-icons">
                      {options?.facebook && (
                        <span className="dhp-social-icon" onClick={openFacebook}>
                          <IonIcon icon={logoFacebook} />
                        </span>
                      )}
                      {options?.instagram && (
                        <span className="dhp-social-icon" onClick={openInstagram}>
                          <IonIcon icon={logoInstagram} />
                        </span>
                      )}
                      {options?.site_url && (
                        <span className="dhp-social-icon" onClick={openWebsite}>
                          <IonIcon icon={globeOutline} />
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="dhp-location-map">
                {options?.position ? (
                  (() => {
                    let lat, lon;
                    try {
                      // Try parsing as JSON string
                      const pos = JSON.parse(options.position);
                      if (pos && typeof pos.lat === 'number' && typeof pos.lng === 'number') {
                        lat = pos.lat;
                        lon = pos.lng;
                      }
                    } catch (e) {
                      // Fallback to comma-separated string
                      const coords = options.position.split(',').map((coord) => parseFloat(coord.trim()));
                      if (coords.length >= 2) {
                        lat = coords[0];
                        lon = coords[1];
                      }
                    }

                    if (lat !== undefined && lon !== undefined && !isNaN(lat) && !isNaN(lon)) {
                      const offset = 0.01;
                      const bbox = `${lon - offset},${lat - offset},${lon + offset},${lat + offset}`;
                      const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`;
                      return (
                        <iframe
                          src={osmUrl}
                          width="100%"
                          height="350"
                          style={{ border: 0, borderRadius: '12px' }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Posizione Farmacia"
                        />
                      );
                    }
                    return null;
                  })()
                ) : options?.google_maps_url ? (
                  <iframe
                    src={options.google_maps_url}
                    width="100%"
                    height="350"
                    style={{ border: 0, borderRadius: '12px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Posizione Farmacia"
                  />
                ) : (
                  <div className="dhp-map-placeholder">
                    <IonIcon icon={locationOutline} />
                    <p>Posizione in arrivo</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer className="dhp-footer">
          <div className="dhp-container">
            <p className="dhp-footer-name">{options?.shop_name}</p>
            <div className="dhp-footer-links">
              <a href="/app/privacy">Privacy Policy</a>
              <span>•</span>
              <a href="/app/tos">Termini di Servizio</a>
            </div>

            <p className="dhp-footer-copy">
              © {new Date().getFullYear()} {options?.shop_name}. Tutti i diritti riservati.
            </p>
          </div>
        </footer>
      </IonContent>
    </>
  );
};

export default DesktopHomePage;
